import { Injectable, signal } from '@angular/core';
import { ScheduleServices } from '../../models/schedule/schedule-services.model';
import { SuggestedServicesModel } from '../../models/services/ai-suggestions/suggested-services.model';
import { AiRecommendationsRequestModel } from '../../models/schedule/ai-recommendations.model';

type ConnectivityStatus = 'CONNECTED' | 'DISCONNECTED';

/**
 * Serviço de estado para as sugestões de IA.
 *
 * Responsabilidades:
 * - Armazena a resposta completa de sugestões da IA (populada pelo RevisionServicesService
 *   sempre que o endpoint com IA é chamado, de forma centralizada).
 * - Armazena os IDs dos serviços da IA que estão atualmente selecionados.
 * - Expõe métodos para sincronizar o estado com o ServiceStorage (por ex. após voltar de um step).
 *
 * Este serviço NÃO acessa o ServiceStorage diretamente para evitar acoplamento circular;
 * a sincronização é iniciada pelo ServicesComponent / AiServiceSuggestionsComponent.
 */
@Injectable({ providedIn: 'root' })
export class AiSuggestionsStateService {
  private readonly _suggestedServices = signal<SuggestedServicesModel | null>(null);
  private readonly _suggestedServiceIds = signal<number[]>([]);
  private readonly _selectedIds = signal<Set<number>>(new Set());

  /**
   * IDs de TODOS os serviços recomendados pela IA, sem o corte de exibição (hoje limitado a 5).
   * Usado apenas para telemetria (endpoint /ai-recommendations) — a UI continua usando
   * `suggestedServiceIds`, que reflete somente os itens de fato apresentados ao usuário.
   */
  private readonly _recommendedServiceIds = signal<number[]>([]);

  /**
   * Indica se o modal de aviso "serviços sugeridos não selecionados" já foi exibido ao
   * usuário na jornada atual. Usado para compor o campo `disclaimerShown` da telemetria.
   */
  private readonly _disclaimerShown = signal(false);

  /** Resposta completa de sugestões da IA (somente leitura). Null quando não há sugestões carregadas. */
  readonly suggestedServices = this._suggestedServices.asReadonly();

  /** IDs dos serviços sugeridos pela IA (somente leitura). */
  readonly suggestedServiceIds = this._suggestedServiceIds.asReadonly();

  /** IDs dos serviços sugeridos que estão selecionados (somente leitura). */
  readonly selectedIds = this._selectedIds.asReadonly();

  /** IDs de todos os serviços recomendados pela IA, sem limite de exibição (somente leitura). */
  readonly recommendedServiceIds = this._recommendedServiceIds.asReadonly();

  /** Se o modal de aviso já foi exibido nesta jornada (somente leitura). */
  readonly disclaimerShown = this._disclaimerShown.asReadonly();

  /**
   * Define a resposta completa de sugestões da IA ao carregar (ou recarregar) os dados do endpoint.
   * Chamado centralmente pelo RevisionServicesService.getServicesByFeatureFlag().
   * Passar `null` limpa o estado (ex.: quando a feature flag está inativa).
   *
   * @param model - Sugestões já limitadas para exibição (o que o usuário efetivamente vê).
   * @param allRecommendedServiceIds - IDs de TODOS os serviços recomendados pela IA, antes do
   * corte de exibição. Usado apenas para telemetria. Se omitido, assume os mesmos IDs de `model`.
   */
  setSuggestedServicesModel(
    model: SuggestedServicesModel | null,
    allRecommendedServiceIds: number[] = []
  ): void {
    this._suggestedServices.set(model);
    this._suggestedServiceIds.set(model?.services.map((s) => s.serviceId) ?? []);
    this._recommendedServiceIds.set(
      allRecommendedServiceIds.length ? allRecommendedServiceIds : model?.services.map((s) => s.serviceId) ?? []
    );
    this._selectedIds.set(new Set());
    this._disclaimerShown.set(false);
  }

  /** Marca que o modal de aviso de serviços não selecionados foi exibido ao usuário. */
  markDisclaimerShown(): void {
    this._disclaimerShown.set(true);
  }

  /** Retorna true se o serviço está na lista de sugestões da IA. */
  isSuggested(serviceId: number): boolean {
    return this._suggestedServiceIds().includes(serviceId);
  }

  /** Retorna true se o serviço sugerido está atualmente selecionado. */
  isSelected(serviceId: number): boolean {
    return this._selectedIds().has(serviceId);
  }

  /** Marca um serviço como selecionado. */
  select(serviceId: number): void {
    const current = this._selectedIds();
    if (!current.has(serviceId)) {
      this._selectedIds.set(new Set([...current, serviceId]));
    }
  }

  /** Remove a seleção de um serviço. */
  deselect(serviceId: number): void {
    const current = this._selectedIds();
    if (current.has(serviceId)) {
      const next = new Set(current);
      next.delete(serviceId);
      this._selectedIds.set(next);
    }
  }

  /** Alterna a seleção de um serviço. */
  toggle(serviceId: number): void {
    if (this.isSelected(serviceId)) {
      this.deselect(serviceId);
    } else {
      this.select(serviceId);
    }
  }

  /**
   * Sincroniza o estado de seleção com os serviços já gravados no storage.
   * Chamado pelo ServicesComponent após carregar/salvar serviços para garantir
   * consistência (inclusive na navegação de volta para este step).
   *
   * @param offeredServices - Serviços opcionais atualmente selecionados no storage.
   * @param selectedRevision - Revisão atualmente selecionada no storage (opcional).
   * @param recallServices - Serviços de recall presentes (incluídos quando recallExists=true).
   */
  syncFromStorage(
    offeredServices: ScheduleServices[],
    selectedRevision?: ScheduleServices,
    recallServices: ScheduleServices[] = []
  ): void {
    const suggestedIds = this._suggestedServiceIds();
    if (!suggestedIds.length) {
      return;
    }

    const newSelected = new Set<number>();

    [...(offeredServices ?? []), ...recallServices].forEach((s) => {
      if (suggestedIds.includes(s.serviceId)) {
        newSelected.add(s.serviceId);
      }
    });

    if (selectedRevision && suggestedIds.includes(selectedRevision.serviceId)) {
      newSelected.add(selectedRevision.serviceId);
    }

    this._selectedIds.set(newSelected);
  }

  /**
   * Monta o payload para o endpoint POST /schedules/{scheduleId}/ai-recommendations,
   * usado para medir a efetividade das sugestões de IA (aceitas x ignoradas).
   *
   * Retorna `null` quando nenhuma sugestão de IA foi carregada nesta jornada (por ex.
   * feature flag desativada), caso em que a chamada ao endpoint deve ser evitada.
   *
   * @param vin - Chassi do veículo.
   * @param connectivityStatus - Se o veículo é conectado ('CONNECTED') ou não ('DISCONNECTED').
   * @param country - País do usuário.
   */
  buildAiRecommendationsRequest(
    vin: string,
    connectivityStatus: ConnectivityStatus,
    country: string
  ): AiRecommendationsRequestModel | null {
    const model = this._suggestedServices();
    const recommendedIds = this._recommendedServiceIds();

    if (!model || !recommendedIds.length) {
      return null;
    }

    const presentedIds = this._suggestedServiceIds();
    const selected = this._selectedIds();
    const acceptedIds = presentedIds.filter((id) => selected.has(id));
    const ignoredIds = presentedIds.filter((id) => !selected.has(id));

    const toServiceItems = (ids: number[]) => ids.map((serviceId) => ({ serviceId }));

    return {
      vin,
      connectivityStatus,
      country,
      disclaimerShown: this._disclaimerShown(),
      recommendedServices: toServiceItems(recommendedIds),
      presentedServices: toServiceItems(presentedIds),
      acceptedServices: toServiceItems(acceptedIds),
      ignoredServices: toServiceItems(ignoredIds),
      justification: model.justification ?? [],
      personaVersion: model.personaVersion,
    };
  }
}
